import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Step from '@material-ui/core/Step';
import StepContent from '@material-ui/core/StepContent';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ReviewCart from 'Agent/AgentForms/Marketing/components/ReviewCart';
import VCConfirmation from 'Agent/AgentForms/Marketing/components/VCConfirmation';
import VCShippingAddress from 'Agent/AgentForms/Marketing/components/VCShippingAddress';
import VCShippingDetails from 'Agent/AgentForms/Marketing/components/VCShippingDetails';
import VCSubmission from 'Agent/AgentForms/Marketing/components/VCSubmission';
import 'Agent/AgentForms/Marketing/components/ViewCart.sass';
import ViewCartYourInfo from 'Agent/AgentForms/Marketing/components/ViewCartYourInfo';
import {
  changeMissingRequiredFields,
  getStepClassName,
  handleStepBack,
  handleSubmitOrder,
  isAddressValidated,
  MarketingViewCart,
  stepHandleNext,
} from 'Agent/AgentForms/Marketing/ducks/Marketing';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {ViewCartProps} from './MarketingViewCart';
import { themeColors } from 'Theme';

export type ReviewCartProps = {
  cartData: MarketingViewCart;
  setActiveStep: any;
  setUpdatedCart: any;
};

export const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: '1rem',
  },
  button: {
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(1),
  },
  disabledButton: {
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(1),
    cursor: 'not-allowed',
    backgroundColor: themeColors.blue_200,
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
}));

function getSteps() {
  return ['Review Cart', 'Your Information', 'Enter Shipping Address', 'Shipping Detail', 'Confirmation'];
}

export function getStepContent(step: any, data: MarketingViewCart, setActiveStep: any, setUpdatedCart: any) {
  switch (step) {
    case 0:
      return <ReviewCart cartData={data} setActiveStep={setActiveStep} setUpdatedCart={setUpdatedCart} />;
    case 1:
      return <ViewCartYourInfo />;
    case 2:
      return <VCShippingAddress />;
    case 3:
      return <VCShippingDetails />;
    case 4:
      return <VCConfirmation />;
    default:
      return setActiveStep(0);
  }
}

export default function VerticalLinearStepper({setIsViewCartBlock, viewCartDetails}: ViewCartProps) {
  const classes = useStyles();
  const userClaims = useSelector((state: ReducerState) => state.user.profile.userClaims);

  const {
    inCartVisible,
    missingRequiredFields,
    viewCart,
    shippingPriority,
    addressValidationResponse,
    isAddressValidating,
    unValidatedAddressAccepted,
    isPOBoxAddress,
  } = useSelector((state: ReducerState) => state.marketing);
  const [activeStep, setActiveStep] = useState(0);
  const [isOrderSubmitted, setIsOrderSubmitted] = useState(false);
  const [updatedCart, setUpdatedCart] = useState(viewCart);
  const steps = getSteps();
  const dispatch = useDispatch();

  const getHandleNext = () => {
    if (activeStep === 2) {
      if (viewCartDetails.items.size >= 1) {
        return unValidatedAddressAccepted && !(isPOBoxAddress && isAddressValidated(addressValidationResponse))
          ? stepHandleNext(
              dispatch,
              changeMissingRequiredFields,
              activeStep,
              missingRequiredFields,
              viewCartDetails,
              setActiveStep,
              shippingPriority,
              userClaims
            )
          : undefined;
      } else {
        return undefined;
      }
    } else {
      return viewCartDetails.items.size >= 1
        ? stepHandleNext(
            dispatch,
            changeMissingRequiredFields,
            activeStep,
            missingRequiredFields,
            viewCartDetails,
            setActiveStep,
            shippingPriority,
            userClaims
          )
        : undefined;
    }
  };

  const submitOrder = () => {
    handleSubmitOrder(
      dispatch,
      setActiveStep,
      setIsOrderSubmitted,
      viewCart,
      inCartVisible,
      updatedCart,
      setUpdatedCart
    );
  };

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              {viewCartDetails.items.size >= 1 ? (
                <Typography>{getStepContent(index, viewCartDetails, setActiveStep, setUpdatedCart)}</Typography>
              ) : (
                <div id="marketing-forms-drawer-title">Your cart is empty...</div>
              )}
              {isAddressValidating ? (
                <div style={{paddingTop: '1rem', paddingRight: '2rem', paddingLeft: '2rem'}}>
                  <LinearProgress />
                </div>
              ) : (
                <div />
              )}
              <div className={classes.actionsContainer} id="buttons-actions">
                <div hidden={isOrderSubmitted}>
                  {index === 4 ? (
                    <Button
                      variant={viewCartDetails.items.size >= 1 ? 'contained' : 'text'}
                      color="primary"
                      onClick={viewCartDetails.items.size >= 1 ? submitOrder : undefined}
                      className={viewCartDetails.items.size >= 1 ? classes.button : classes.disabledButton}
                    >
                      Submit Order
                    </Button>
                  ) : (
                    <Button
                      id="continue-btn"
                      variant={viewCartDetails.items.size >= 1 ? 'contained' : 'text'}
                      color="primary"
                      onClick={getHandleNext}
                      className={getStepClassName(activeStep, viewCartDetails, unValidatedAddressAccepted, isPOBoxAddress, addressValidationResponse, classes)}
                    >
                      Continue
                    </Button>
                  )}
                  <Button
                    id="cancel-btn"
                    onClick={e =>
                      activeStep <= 0
                        ? setIsViewCartBlock(false)
                        : handleStepBack(dispatch, changeMissingRequiredFields, setActiveStep)
                    }
                    className={classes.button}
                    color="primary"
                    variant={viewCartDetails.items.size >= 1 ? 'text' : 'contained'}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {isOrderSubmitted && <VCSubmission setIsViewCartBlock={setIsViewCartBlock} />}
    </div>
  );
}
